/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import type { SearchStatus } from '../context/PartnershipsWizardContext';

interface SearchProgressProps {
  status: SearchStatus;
  agenciesFound: number;
  propertiesRetrieved: number;
}

/**
 * Live progress panel for an in-flight partner search, showing the current
 * phase and (while running) the agencies-found / properties-retrieved counters.
 *
 * @param props - The search status and running counters.
 * @returns The search progress panel.
 */
export function SearchProgress({
  status,
  agenciesFound,
  propertiesRetrieved,
}: SearchProgressProps) {
  const { t } = useTranslation('features/partnerships');

  const isPending = status === 'pending';
  const isRunning = status === 'running';

  return (
    <div
      className={cn(
        'flex flex-col gap-[var(--space-3)] rounded-(--radius-lg)',
        'border border-[var(--border-subtle)] bg-[var(--surface-secondary)]',
        'p-[var(--space-5)] shadow-[var(--shadow-sm)]',
      )}
      data-testid="search-progress"
    >
      <div className="flex items-center gap-[var(--space-2)]">
        <div className="h-2 w-2 animate-pulse rounded-full bg-[var(--seed-blue)]" />
        <Text variant="body" size="sm" fw={600}>
          {isPending && t('progress.pending')}
          {isRunning && t('progress.running')}
        </Text>
      </div>

      {isRunning && (
        <div className="flex gap-[var(--space-4)]">
          <CounterPill
            label={t('progress.agencies_found')}
            value={agenciesFound}
          />
          <CounterPill
            label={t('progress.properties_retrieved')}
            value={propertiesRetrieved}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Small inline "label: value" counter pill used inside {@link SearchProgress}.
 *
 * @param props - The counter label and numeric value.
 * @returns The counter pill.
 */
function CounterPill({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center gap-[var(--space-1)]">
      <Text variant="body" size="xs" className="text-[var(--text-secondary)]">
        {label}:
      </Text>
      <Text variant="body" size="xs" fw={700}>
        {value}
      </Text>
    </div>
  );
}
